import React, { useEffect, useRef, useState } from "react";
import { BiSolidChevronDown } from "react-icons/bi";
import { Popover, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchAccounts } from "../../store/Slices/AccountSlice";

const formatBalance = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const accountTypeLabel = (type) => (Number(type) === 1 ? "Cash" : "Bank");

const PaymentAccountSelector = ({
  selectedAccount,
  setSelectedAccount,
  placeholder = "Select Account",
  label,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const dispatch = useDispatch();
  const AccountState = useSelector((state) => state.AccountState);

  useEffect(() => {
    dispatch(fetchAccounts());
  }, [dispatch]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "payment-account-popover" : undefined;

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => searchInputRef.current?.focus(), 80);
    return () => clearTimeout(timer);
  }, [open]);

  const accounts = Array.isArray(AccountState.data) ? AccountState.data : [];

  const filtered = accounts.filter((account) => {
    if (!searchText.trim()) return true;
    const q = searchText.trim().toLowerCase();
    return (
      String(account.account_name || "").toLowerCase().includes(q) ||
      String(account.account_no || "").includes(q)
    );
  });

  const displayName = selectedAccount?.account_name
    ? `${selectedAccount.account_name} (${accountTypeLabel(selectedAccount.type)})`
    : "";

  return (
    <div className="flex flex-col px-0 gap-y-1 relative w-full">
      {label && (
        <p className="text-sm font-bold text-main px-1">{label}</p>
      )}
      <div
        ref={triggerRef}
        className="relative text-left cursor-pointer transition-colors ease-in-out duration-200 border border-line hover:border-main bg-card text-ink px-4 py-3 rounded-lg font-quicksand font-bold text-sm flex gap-x-6 items-center active:scale-[.99]"
        onClick={handleClick}
      >
        <div className="flex items-center justify-between w-full min-w-0">
          <div className="w-full outline-none text-left bg-transparent truncate">
            {selectedAccount ? displayName : placeholder}
          </div>
          <BiSolidChevronDown className="text-[1.5rem] cursor-pointer text-muted shrink-0" />
        </div>
      </div>
      {selectedAccount && (
        <p className="text-xs text-muted px-1">
          Balance: Rs {formatBalance(selectedAccount.amount)}
        </p>
      )}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            border: "1px solid #E5E9EB",
            width: anchorEl ? anchorEl.offsetWidth : "auto",
            overflow: "hidden",
            marginTop: "4px",
            boxShadow: "0 10px 30px rgba(16,69,70,0.12), 0 2px 8px rgba(16,69,70,0.08)",
            maxHeight: "50vh",
            zIndex: 1500,
          },
        }}
        sx={{ zIndex: 1500 }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Typography
          sx={{
            p: 2,
            backgroundColor: "#ffffff",
            overflow: "hidden",
            borderRadius: "12px",
          }}
        >
          <div className="bg-card text-ink font-quicksand flex flex-col justify-center items-center rounded-lg">
            <div className="w-full flex flex-col justify-between gap-y-3 items-start">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search account"
                className="w-full px-3 py-2 rounded-xl text-ink"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <div className="flex flex-col gap-y-2 h-[40vh] overflow-y-auto w-full">
                {filtered.length === 0 ? (
                  <p className="text-sm text-white/80 px-1">No accounts found</p>
                ) : (
                  filtered.map((account) => (
                    <div
                      key={account._id}
                      className="flex gap-x-3 items-start cursor-pointer w-full py-1 hover:bg-subtle rounded-lg px-2 transition-colors"
                      onClick={() => {
                        setSelectedAccount(account);
                        handleClose();
                      }}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border border-line accent-main shrink-0"
                        checked={
                          String(selectedAccount?._id || "") ===
                          String(account._id || "")
                        }
                        readOnly
                        onChange={() => {}}
                      />
                      <div className="min-w-0">
                        <span className="font-semibold block truncate">
                          {account.account_name}
                        </span>
                        <span className="text-xs text-white/80">
                          {accountTypeLabel(account.type)} · Rs{" "}
                          {formatBalance(account.amount)}
                          {account.account_no ? ` · #${account.account_no}` : ""}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </Typography>
      </Popover>
    </div>
  );
};

export default PaymentAccountSelector;
